import { useState, useCallback } from 'react';

import { supabase } from '../../../lib/supabase';
import type { GameRoom } from '../../../types/multiplayer';

/**
 * Hook for resetting the multiplayer room state (e.g., after game end or by host action).
 */
export function useRoomReset(roomId: string | null) {
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Reset the room state in Supabase and locally
  const resetRoom = useCallback(async () => {
    if (!roomId) return false;
    setResetting(true);
    setResetError(null);
    try {
      // Reset room status and clear session/answers as needed
      const { error: roomError } = await supabase
        .from('multiplayer_game_rooms')
        .update({
          status: 'waiting',
          current_session_id: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', roomId);
      if (roomError) throw roomError;

      // Optionally, clear answers, player readiness, etc.
      await supabase
        .from('multiplayer_game_room_players')
        .update({ is_ready: false })
        .eq('room_id', roomId);
      // Optionally, clear other tables (e.g., answers, chat, etc.)

      setResetting(false);
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to reset room';
      setResetError(msg);
      setResetting(false);
      return false;
    }
  }, [roomId]);

  return { resetRoom, resetting, resetError };
}

export default useRoomReset;
